// src/controllers/actionController.js

import useSelectionStore from "@store/useSelectionStore";
import useGameStore from "@store/useGameStore";
import useAuthStore from "@store/useAuthStore";
import useMapStore from "@store/useMapStore";
import { planAction, cancelPlannedActionApi } from "@api/actionApi";
import feedback from "@components/ui/general/feedback";
import { canUseAction } from "@helpers/game/canUseAction";
import { getLastUnitPosition } from "@helpers/game/calculateActionOrigins";
import calculateTargetableTiles from '@helpers/game/calculateTargetableTiles';
import calculateAoeTiles, { getUnitsInAoe } from '@helpers/game/calculateAoeTiles';
import { getAoeInfo } from '@utils/statIcons';

/**
 * ✅ REFACTORIZADO - Entra en modo targeting para una acción específica
 * Calcula y muestra directamente los tiles válidos desde la posición correcta
 */
export function enterTargetingMode(action, selectedUnit) {
  const { game } = useGameStore.getState();
  const { setInteractionMode, setHighlightTiles, setOverlayMode } = useMapStore.getState();
  const { setTargetingAction } = useSelectionStore.getState();

  if (!selectedUnit || !game || !action) {
    console.warn("[actionController] Cannot enter targeting mode: missing data");
    return;
  }

  /*console.info("[actionController] 🎯 Entrando en modo targeting:", {
    action: action.slug,
    unit: selectedUnit.id,
    unitPhysicalPosition: { x: selectedUnit.x, y: selectedUnit.y }
  });*/

  // ✅ Calcular la última posición conocida de la unidad (después de acciones planificadas)
  const lastPosition = getLastUnitPosition(game, selectedUnit.id);
  const actionOrigin = lastPosition || { x: selectedUnit.x, y: selectedUnit.y };
  
  /*console.info("[actionController] 📍 Posición para targeting calculada:", {
    unitId: selectedUnit.id,
    physicalPosition: { x: selectedUnit.x, y: selectedUnit.y },
    lastKnownPosition: lastPosition,
    actionOrigin: actionOrigin,
    hasPlannedActions: !!lastPosition && (lastPosition.x !== selectedUnit.x || lastPosition.y !== selectedUnit.y)
  });*/

  // ✅ Calcular tiles válidos desde la posición correcta
  const targetableTiles = calculateTargetableTiles(action, actionOrigin, game, selectedUnit);

  /*console.info("[actionController] ✅ Tiles válidos calculados:", {
    count: targetableTiles.length,
    fromPosition: actionOrigin
  });*/

  // Check if action has self-origin AOE (like Taunt)
  const aoeInfo = getAoeInfo(action);
  const isSelfAoe = aoeInfo.isAoe && aoeInfo.aoeData?.origin === 'self';

  // ✅ For self-targeted actions (with or without AOE), auto-execute immediately
  // Hover already shows AOE preview, so single click should execute
  if (action.target_type === 'self') {
    planAt(actionOrigin.x, actionOrigin.y, action);
    return;
  }

  // ✅ Actualizar estados directamente
  setTargetingAction(action);
  setInteractionMode("targeting");
  setHighlightTiles(targetableTiles);
  setOverlayMode(action.slug);
}

/**
 * Sale del modo targeting
 */
export function exitTargetingMode() {
  const { clearTargeting } = useSelectionStore.getState();
  const { setInteractionMode, clearVisuals, clearAoePreview } = useMapStore.getState();

  //console.info("[actionController] 🚪 Saliendo del modo targeting");

  clearTargeting();
  setInteractionMode("select");
  clearVisuals();
  clearAoePreview();
}

/**
 * Execute a self-AOE action (like Taunt) after showing preview
 * @param {object} action - The action to execute
 * @param {object} unit - The unit executing the action
 */
export function executeSelfAoeAction(action, unit) {
  const { game } = useGameStore.getState();
  if (!game || !action || !unit) {
    console.warn("[actionController] Cannot execute self-AOE: missing data");
    return;
  }

  const lastPosition = getLastUnitPosition(game, unit.id);
  const actionOrigin = lastPosition || { x: unit.x, y: unit.y };

  // Clear AOE preview before executing
  const { clearAoePreview } = useMapStore.getState();
  clearAoePreview();

  // Execute the action at unit's position
  planAt(actionOrigin.x, actionOrigin.y, action);
}

/**
 * ✅ REFACTORIZADO - Intenta planificar una acción desde la unidad seleccionada hacia un tile (x, y)
 * @param {number} x - Target X coordinate
 * @param {number} y - Target Y coordinate  
 * @param {object} action - Optional action to use instead of targetingAction (for auto-execution)
 */
export async function planAt(x, y, action = null) {
  const { targetingAction, getSelectedUnit } = useSelectionStore.getState();
  const selectedUnit = getSelectedUnit();  // Call the method
  const { game } = useGameStore.getState();
  const { addProcessingTile, removeProcessingTile } = useMapStore.getState();

  // ✅ Use passed action or fall back to targetingAction
  const actionToUse = action || targetingAction;

  if (!actionToUse || !selectedUnit || !game) {
    console.warn("[actionController] Cannot plan action: missing data.", {
      actionToUse: !!actionToUse,
      targetingAction: !!targetingAction,
      selectedUnit: !!selectedUnit,
      game: !!game,
    });
    feedback.warningOnce(
      "Cannot plan action: missing unit, action, or game data. Please check your selection."
    );
    return;
  }

  // 🔍 DEBUG: Verificar posición antes de planificar
  const currentLastPosition = getLastUnitPosition(game, selectedUnit.id);
  const currentOrigin = currentLastPosition || { x: selectedUnit.x, y: selectedUnit.y };

  /*console.info("[actionController] 🎯 ANTES de planificar acción:", {
    unitId: selectedUnit.id,
    unitPhysicalPosition: { x: selectedUnit.x, y: selectedUnit.y },
    lastKnownPosition: currentLastPosition,
    actionOrigin: currentOrigin,
    actionSlug: actionToUse.slug,
    targetPosition: { x, y },
    isMovementAction: actionToUse.target_type === 'move' || actionToUse.can_move_target,
    isAutoExecution: !!action // ✅ Debug flag for auto-execution
  });*/

  // Block duplicate clicks while processing
  const { isProcessingTile } = useMapStore.getState();
  if (isProcessingTile(x, y)) {
    return;
  }

  // Show processing indicator while API call is pending
  addProcessingTile(x, y, 'action');

  // // TODO: REMOVE - Artificial delay for testing spinner visibility
  // await new Promise(resolve => setTimeout(resolve, 1000));

  try {
    // ✅ FIX: Pass the calculated origin position to the API
    /*console.info("[actionController] 📤 ENVIANDO A BACKEND:", {
      gameId: game.id,
      unitId: selectedUnit.id,
      actionSlug: actionToUse.slug,
      target_data: { x, y },
      origin_data: currentOrigin, // ✅ NOW SENDING CORRECT POSITION
      isAutoExecution: !!action,
      message: `Sending action with origin: (${currentOrigin.x},${currentOrigin.y}) -> target: (${x},${y})`
    });*/
    
    const response = await planAction(game.id, selectedUnit.id, actionToUse.slug, { x, y }, currentOrigin);
    
    if (response?.data?.xhelos) {
      
      
      // 🔍 DEBUG: Verificar posición después de planificar
      const newLastPosition = getLastUnitPosition(response.data.xhelos, selectedUnit.id);
      const newOrigin = newLastPosition || { x: selectedUnit.x, y: selectedUnit.y };
      
      /*console.info("[actionController] 🎯 DESPUÉS de planificar acción:", {
        unitId: selectedUnit.id,
        oldOrigin: currentOrigin,
        newLastPosition: newLastPosition,
        newOrigin: newOrigin,
        positionChanged: JSON.stringify(currentOrigin) !== JSON.stringify(newOrigin),
        isMovementAction: actionToUse.target_type === 'move' || actionToUse.can_move_target
      });*/

      // Verificar si es una acción de movimiento
      if (actionToUse.target_type === 'move' || actionToUse.can_move_target) {
        //console.info("[actionController] 🚶 Acción de movimiento detectada");
        if (JSON.stringify(currentOrigin) === JSON.stringify(newOrigin)) {
          console.warn("[actionController] ⚠️ PROBLEMA: La posición no cambió después de una acción de movimiento!");
        } else {
          //console.info("[actionController] ✅ Posición actualizada correctamente después del movimiento");
        }
      }

      feedback.success(`Action "${actionToUse.name}" planned successfully`);
    }else{
      console.warn("[actionController] ⚠️ No se pudo planificar la acción, respuesta inesperada:", response);
      feedback.error("Failed to plan action");
      return;
    }
  } catch (error) {
    // Error logging and feedback handled centrally by api.js
    // Only show generic message if api.js didn't show one (no userMessage from backend)
    if (!error.userMessage) {
      feedback.error("Failed to plan action");
    }
  } finally {
    // Always remove processing indicator
    removeProcessingTile(x, y);

    // ✅ Handle post-action flow differently for auto-execution vs targeting mode
    if (action) {
      // Auto-executed action (self-targeted) - clear AOE preview
      const { clearAoePreview } = useMapStore.getState();
      clearAoePreview();
    } else {
      // Normal targeting mode - check if we can continue using the action
      if (!canUseAction(actionToUse, selectedUnit)) {
        //console.info("[actionController] No se puede usar más la acción, saliendo del targeting");
        exitTargetingMode();
      } else {
        // Recalcular highlighting para la siguiente acción desde la nueva posición
        //console.info("[actionController] Recalculando targeting para siguiente acción");
        enterTargetingMode(actionToUse, selectedUnit);
      }
    }
  }
}

// ✅ Set para rastrear acciones que se están cancelando para evitar duplicados
const cancellingActions = new Set();

/**
 * Cancela una acción planificada con protección contra condiciones de carrera
 */
export async function cancelAction(gameId, actionId) {
  // ✅ Verificar si ya se está cancelando esta acción
  const cancelKey = `${gameId}-${actionId}`;
  if (cancellingActions.has(cancelKey)) {
    console.warn(`[actionController] ⚠️ Acción ${actionId} ya se está cancelando, ignorando duplicado`);
    return;
  }

  // ✅ Marcar como en proceso de cancelación
  cancellingActions.add(cancelKey);

  try {
    // ✅ Obtener detalles de la acción ANTES de eliminarla para el feedback
    const { getHydratedPlannedActionById } = useGameStore.getState();
    const actionToDelete = getHydratedPlannedActionById(actionId);
    const actionName = actionToDelete?.action?.name || actionToDelete?.action?.slug || 'Unknown Action';
    
    //console.info(`[actionController] Cancelando acción "${actionName}" (ID: ${actionId}) en juego ${gameId}`);
    
    const response = await cancelPlannedActionApi(gameId, actionId);
    
    if (response?.xhelos) {
     
      feedback.success(`Action "${actionName}" cancelled successfully`);
      
      // 🔍 DEBUG: Log para verificar que las posiciones se recalculen correctamente
      console.info(`[actionController] ✅ Acción "${actionName}" cancelada, posiciones recalculadas automáticamente`);
    } else {
      console.warn("[actionController] ⚠️ Respuesta inesperada al cancelar acción:", response);
      feedback.error("Unexpected response when cancelling action");
    }
  } catch (error) {
    // Error logging handled centrally by api.js
    // Handle context-specific error messages
    if (error?.response?.status === 404) {
      console.warn("[actionController] ⚠️ La acción ya fue eliminada o no existe");
      feedback.warning("Action was already cancelled");
    } else if (error?.response?.status === 403) {
      feedback.error("You don't have permission to cancel this action");
    } else {
      feedback.error("Failed to cancel action");
    }
  } finally {
    // ✅ Siempre limpiar el flag, incluso si hay error
    cancellingActions.delete(cancelKey);
  }
}

// Export as object for consistency
const actionController = {
  enterTargetingMode,
  exitTargetingMode,
  executeSelfAoeAction,
  planAt,
  cancelAction
};

export default actionController;

